import {
  Component,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  MatRowDef,
  MatTableModule,
} from "@angular/material/table";
import { MatDialogModule, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatSelectModule } from "@angular/material/select";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatOptionModule } from "@angular/material/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MatMenuModule } from "@angular/material/menu";
import { MatTabsModule } from "@angular/material/tabs";
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from "@ng-select/ng-select";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { ICreateDocumentRange } from "./interfaces/create-document-range.interface";
import { CompanyAttributes, DocumentClassAttributes, DocumentRangeAttributes, DocumentTypeAttributes, EmissionPointAttributes } from "@albatrosdeveloper/ave-models-package";
import { DocumentClassService } from "../document-class/document-class.service";
import { DocumentTypeService } from "../document-type/document-type.service";
import { IUpdateDocumentRange } from "./interfaces/update-document-range.interface";
import { EmissionPointService } from "../emission-point/emission-point.service";
import { DocumentRangeService } from "./document-range.service";
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { slice } from "lodash";
import { ExportService } from "app/layout/export-overlay/export-overlay.service";
import { ExportDialogComponent } from 'app/layout/export-overlay/dialog/export-dialog.component';
import { ExportOptions } from "app/layout/export-overlay/tasks/core/export-task.interfaces";
import { DocumentRangeExport } from "./exports/document-range.export";
import { DOCUMENT_RANGE_TITLE } from "./exports/document-range.constants";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CdkDropList } from "@angular/cdk/drag-drop";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { catchError, lastValueFrom, of, Subscription, take } from "rxjs";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { StatusFiltersComponent } from "../../filters/status-filters/status-filters.component";
import { DocumentsFiltersComponent } from "../../filters/documents-filters/documents-filters.component";
import { ActivatedRoute } from "@angular/router";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { CompanyService } from "../../configuration/company/company.service.service";
import { FormSectionComponent } from "app/shared/components/form-ui/form-section/form-section.component";
import { ToggleOptionComponent } from "app/shared/components/form-ui/toggle-option/toggle-option.component";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FormDocumentRangeComponent } from "./form-document-range/form-document-range.component";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { ConfirmationService } from "app/shared/services/confirmation.service";
import { AuditLogViewerService } from "app/shared/services/audit-log-viewer.service";
import { AUDIT_LOG_COLLECTIONS } from "app/shared/components/audit-log-viewer/audit-log-collections.constants";
import { HasPermissionPipe, PermissionDirective, PermissionService } from "app/core/permission";
import { DOCUMENT_RANGE_PERMISSIONS } from "./document-range.permissions";

interface DocumentRangeNode extends DocumentRangeAttributes {
  children?: DocumentRangeNode[];
}

interface DocumentRangeFlatNode extends DocumentRangeAttributes {
  expandable: boolean;
  levelTree: number;
}

@Component({
  selector: "app-document-range",
  templateUrl: "./document-range.component.html",
  styleUrls: ["./document-range.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgSelectComponent,
    NgSelectModule,
    MatDatepickerModule,
    MatTabsModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MatTreeModule,
    FilterComponent,
    StatusFiltersComponent,
    DocumentsFiltersComponent,
    NgxMaskDirective,
    FormSectionComponent,
    ToggleOptionComponent,
    HoverMenuDirective,
    PermissionDirective,
    HasPermissionPipe,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  provideNativeDateAdapter(),
    DocumentRangeExport,
    provideNgxMask()
  ],
})
export class DocumentRangeComponent implements AfterViewInit, OnDestroy, OnInit {
  readonly DOCUMENT_RANGE_PERMISSIONS = DOCUMENT_RANGE_PERMISSIONS;

  displayedColumns: string[] = ["code", "documentType", "documentClass", "dateEnd", "upperLimit", "currentValue", "status", "actions"];

  dataSource: DocumentRangeAttributes[] = [];
  documentRangeList: DocumentRangeAttributes[] = [];
  selectedCompanyIds: string[] = [];
  selectedEmissionPointIds: string[] = [];
  selectedDocumentTypeIds: string[] = [];
  selectedDocumentClassIds: string[] = [];
  companyList: CompanyAttributes[] = [];

  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  validFormR: boolean = false;
  validFormInitial: boolean = false;
  validFormReach: boolean = false;
  validFormDate: boolean = false;
  validFormRange: boolean = false;
  validFormFormat: boolean = false;

  private transformer = (node: DocumentRangeNode, levelTree: number): DocumentRangeFlatNode => {
    return {
      ...node,
      expandable: node.children && node.children.length > 0,
      levelTree: levelTree,
    };
  };

  treeControl = new FlatTreeControl<DocumentRangeFlatNode>(
    node => node.levelTree, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this.transformer, node => node.levelTree,
    node => node.expandable, node => node.children);

  displayedColumnsTree: string[] = ["code", "documentType", "documentClass", "dateEnd", "upperLimit", "currentValue", "status", "actions"];
  dataSourceTree = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  auxData: DocumentRangeAttributes[] = []

  modeTree: boolean = false

  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;
  @ViewChild('listGroup', { static: false }) list1: CdkDropList;

  //BUSCADOR
  searchSubscription: Subscription;

  selectedStatuses: [] = [];
  form: FormGroup;

  // Lazy loading flags
  loadFiltersData = false;
  documentTypesLoaded = false;
  documentClassesLoaded = false;
  emissionPointsLoaded = false;
  documentRangesLoaded = false;

  documentTypeList: DocumentTypeAttributes[] = [];
  emissionPointList: EmissionPointAttributes[] = [];

  constructor(
    private emisionPointService: EmissionPointService,
    private readonly documentRangeService: DocumentRangeService,
    private documentTypeService: DocumentTypeService,
    private readonly documentClassService: DocumentClassService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public exportService: ExportService,
    public documentRangeExport: DocumentRangeExport,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private handlerMessage: HandlerMessageService,
    private confirmationService: ConfirmationService,
    private readonly auditLogViewerService: AuditLogViewerService,
    private readonly permissionService: PermissionService
  ) {
    this.form = this.fb.group({
      documentClassId: [],
      pointEmissionId: [],
      documentTypeId: []
    });

    this.route.queryParams.subscribe(params => {
    const pointEmissionId = params['pointEmissionId'] || null;

    if (pointEmissionId) {
      this.form.get('pointEmissionId').setValue([Number(pointEmissionId)]);
    } 
  });
  }

  async ngOnInit(): Promise<void> {
    await this.getAllCompany();
  }

  ngAfterViewInit(): void {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.VIEW, "No tienes permisos para ver rangos de documento.")) {
      return;
    }

    this.fetchAllDocumentType();
    this.fetchAllEmissionPoint();
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }

  // private fetchAllConditionExp(): void {
  //   this.expeditionConditionService.findToComboBox().subscribe({
  //     next: (res) => {
  //       this.expeditionConditionList = res.data;
  //     },
  //     error: (err) => {
  //     },
  //   });
  // }

  private toServiceError(error: any, fallbackMessage: string): { message: string; status?: number } {
    const backendMessage =
      typeof error?.error?.message === "string" ? error.error.message.trim() : undefined;
    const nativeMessage =
      typeof error?.message === "string" ? error.message.trim() : undefined;

    return {
      message: backendMessage || nativeMessage || fallbackMessage,
      status: error?.status,
    };
  }

  private fetchAllDocumentType(): Promise<void> {
    if (this.documentTypesLoaded) return Promise.resolve();
    this.documentTypesLoaded = true;

    return new Promise((resolve, reject) => {
      this.documentTypeService
        .findToComboBox()
        .pipe(
          catchError((error) => {
            const serviceError = this.toServiceError(
              error,
              "Error al obtener tipos de documentos."
            );
            this.handlerMessage.error({
              message: serviceError.message,
              alternativeMessage: "Error al obtener tipos de documentos.",
              statusCode: serviceError.status,
            });
            reject(error);
            return of({ data: [] });
          })
        )
        .subscribe({
          next: (res) => {
            this.documentTypeList = res.data ?? [];
            resolve();
          },
        });
    });
  }

  private fetchAllEmissionPoint(): void {
    if (this.emissionPointsLoaded) return;
    this.emissionPointsLoaded = true;

    this.emisionPointService
      .findToComboBox()
      .pipe(
        catchError((error) => {
          const serviceError = this.toServiceError(
            error,
            "Error al obtener puntos de emisión."
          );
          this.handlerMessage.error({
            message: serviceError.message,
            alternativeMessage: "Error al obtener puntos de emisión.",
            statusCode: serviceError.status,
          });
          return of({ data: [] });
        })
      )
      .subscribe({
        next: (res) => {
          this.emissionPointList = res?.data ?? [];
        },
      });
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    // const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" =
      this.sort.direction ? this.sort.direction.toUpperCase() === "ASC" ? "ASC" : "DESC" : "ASC";
    const documentTypes = this.form.get("documentTypeId").value;
    const documentClassIds = this.form.get("documentClassId").value;
    const emissionPointIds = this.form.get("pointEmissionId").value;

    this.documentRangeService
      .findAllWithCount({
        currentPage: this.pageIndex,
        limitPerPage: this.pageSize,
        search: this.searchQuery,
        sort: sortField,
        sortDirection: sortDirection,
      },
        documentTypes,
        documentClassIds,
        emissionPointIds,
        this.modeTree,
        this.selectedStatuses
      )
      .subscribe((data) => {
        this.auxData = data?.data?.[0]?.data ?? [];
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        if (this.paginator) {
          this.paginator.length = this.totalItems;
        }
        // Tree
        let tempTree = []
        const uniqueDocumentRangeByEmissionPoints: DocumentRangeAttributes[] = [];
        const seenEmissionPointIds = new Set<number>();

        // Separo por distinto punto de emision
        for (const item of (data?.data?.[0]?.data ?? [])) {
          const documentRangeByEmissionPoint = item;
          if (!seenEmissionPointIds.has(documentRangeByEmissionPoint.emissionPoint.id)) {
            seenEmissionPointIds.add(documentRangeByEmissionPoint.emissionPoint.id);
            uniqueDocumentRangeByEmissionPoints.push(documentRangeByEmissionPoint);
          }
        }

        let principals = uniqueDocumentRangeByEmissionPoints;

        // Armo arbol hasta nivel 1 de puntos de emision
        for (const ep of principals) {
          let node = {
            ...ep,
            children: [],
          }

          this.getTree(node, node.emissionPoint.id)
          tempTree.push(node)
        }

        this.dataSourceTree.data = tempTree

      });
  }

  getTree(data: DocumentRangeNode, emissionParentId: number) {
    let auxData = this.auxData

    data.children = [];
    auxData.forEach(res => {
      if (res.emissionPoint.id == emissionParentId) {
        data.children.push({
          ...res
        })
      }
    })
  }

  private getSortField(): keyof DocumentRangeAttributes {
    const validSortFields: (keyof DocumentRangeAttributes)[] = [
      "code",
      "documentType.name" as keyof DocumentRangeAttributes,
      "documentClass.name" as keyof DocumentRangeAttributes,
      "currentValue",
      "statusId",
      "upperLimit"
    ];
    return validSortFields.includes(
      this.sort.active as keyof DocumentRangeAttributes
    )
      ? (this.sort.active as keyof DocumentRangeAttributes)
      : "code";
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex + 1;
    this.findAllWithCount();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      // this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      });
  }

  trackByFn(index: number, item: DocumentRangeAttributes): string {
    return item._id.toString();
  }

  openModal(item?: DocumentRangeAttributes){
    const dialogRef = this.dialog.open(FormDocumentRangeComponent, {
      width: '900px',
      disableClose: true,
      data: item || null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.findAllWithCount()
      }
    });
  }
  
  onStatusChange(event: boolean, condition: DocumentRangeAttributes): void {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.UPDATE, "No tienes permisos para cambiar el estado de rangos de documento.")) {
      return;
    }

    const originalStatusId = condition.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedDocumentRange: IUpdateDocumentRange = { statusId: updatedStatusId };

    this.documentRangeService
      .update(condition._id.toString(), updatedDocumentRange)
      .subscribe({
        next: () => {
          this.findAllWithCount();
        },
        error: () => {
          condition.statusId = originalStatusId; // Volver al estado original si falla
          this.snackBar.open("Error al actualizar el estado.", "Cerrar", {
            duration: 3000,
          });
          this.findAllWithCount();
        },
      });
  }

  customSearchFn(term: string, item: any): boolean {
    term = term.toLowerCase();
    return item.code.toLowerCase().includes(term) || item.name.toLowerCase().includes(term);
  }

  openAuditLog(documentRange: DocumentRangeAttributes): void {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.VIEW_AUDIT_LOG, "No tienes permisos para ver la bitácora de rangos de documento.")) {
      return;
    }

    this.auditLogViewerService.open({
      documentIdentifiers: { id: documentRange.id },
      entityName: documentRange.code,
      collectionName: AUDIT_LOG_COLLECTIONS.DOCUMENT_RANGE,
    });
  }

  openDeleteDialog(condition: DocumentRangeAttributes): void {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.DELETE, "No tienes permisos para eliminar rangos de documento.")) {
      return;
    }

    this.dialog
      .open(this.deleteDialog, { data: condition, panelClass: "delete-modal", disableClose: true })
      .afterClosed()
      .subscribe((result) => result && this.delete(condition._id.toString()));
  }

  delete(id: string): void {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.DELETE, "No tienes permisos para eliminar rangos de documento.")) {
      return;
    }

    this.documentRangeService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open("Registro eliminado con éxito", "Cerrar", {
        duration: 3000,
      });
    });
  }

  compareById(item1: any, item2: any): boolean {
    return item1 && item2 ? item1._id === item2._id : item1 === item2;
  }

  clearFilters(): void {
    this.selectedDocumentClassIds = [];
    this.selectedDocumentTypeIds = [];
    this.selectedEmissionPointIds = [];
    this.searchQuery = "";

    this.form = this.fb.group({
      documentClassId: [],
      pointEmissionId: [],
      documentTypeId: []
    });
    this.findAllWithCount();
  }

  // onExportButtonPressed() {
  //   const id = 'document-range';
  //   if (this.exportService.taskIsActive(id)) {
  //     this.exportService.setVisible(true);
  //     return;
  //   }

  //   const dialogRef = this.dialog.open(ExportDialogComponent);
  //   dialogRef.afterClosed().subscribe((options: ExportOptions) => {
  //     if (options) {
  //       this.exportService.createForDocumentRangeList(
  //         this.totalItems,
  //         this.documentRangeService.currentFilter,
  //         options.range,
  //         options.format,
  //       ).start();
  //     }
  //   });
  // }

  onExportButtonPressed() {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.EXPORT, "No tienes permisos para exportar rangos de documento.")) {
      return;
    }

    if (!this.documentRangeExport.canStartTask()) return;
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: DOCUMENT_RANGE_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.documentRangeExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
        );
      }
    });
  }

  getCompanyFromEmissionPoint(emissionPointId: string): CompanyAttributes | undefined {
    const emissionPoint = this.emissionPointList?.find(
      (emissionPoint) => emissionPoint?._id?.toString() === emissionPointId
    );

    const emissionPointCompanyEmbeded = emissionPoint?.company;
    const company = this.companyList?.find(
      (company) => company?._id?.toString() === (emissionPointCompanyEmbeded ? emissionPointCompanyEmbeded?._id?.toString() : '')
    );

    return company;
  }

  isDocumentTypePedido(documentTypeId: string): boolean {
    if (!documentTypeId) return false;    

    const docType = this.documentTypeList.find(dt => String(dt._id) === documentTypeId);
    
    return docType?.code?.toUpperCase() === DocumentTypeEnum.PEDIDO;
  }

  getMaxNumericCharacters(emissionPointId): number {
    const selectedCompany = this.getCompanyFromEmissionPoint(emissionPointId);

    const result = selectedCompany?.documentNumberLength || 8;
    return result;
  }

  generateExampleColumnsFormat(register: DocumentRangeAttributes): string {
    const leftFormat = register.leftFormat || '';
    const rightFormat = register.rightFormat || '';
    const isPedido = register.documentType?._id 
      ? this.isDocumentTypePedido(register.documentType._id.toString()) 
      : false;

    const maxDigits = register.emissionPoint?._id 
      ? this.getMaxNumericCharacters(register.emissionPoint._id.toString()) 
      : 0;

    let numericPart = '';

    if (register.currentValue && register.currentValue > 0) {
      const currentValueStr = register.currentValue.toString();
      const zerosNeeded = Math.max(maxDigits - currentValueStr.length, 0);
      numericPart = '0'.repeat(zerosNeeded) + currentValueStr;
    } else {
      numericPart = '0'.repeat(maxDigits);
    }

    const prefix = isPedido ? 'LD-' : '';

    const example = `${prefix}${leftFormat}${numericPart}${rightFormat}`;
    return example;
  }

  onChangeEmissionPointTree() {
    if (!this.checkIfHasPermission(DOCUMENT_RANGE_PERMISSIONS.TREE_VIEW, "No tienes permisos para usar el modo árbol.")) {
      return;
    }

    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async getAllCompany() {
    const observer = this.companyService.findAllWithCount({
      currentPage: 1,
      limitPerPage: Number.MAX_SAFE_INTEGER,
    }, undefined)
    .pipe(take(1));

    await lastValueFrom(observer);

    observer
    .pipe(take(1))
    .subscribe((res) => {
      this.companyList = res?.data?.[0]?.data;
      this.cdr.detectChanges(); // Detectar cambios después de cargar las empresas
    });

  }

  checkIfHasPermission(permission: string, errorMessage?: string): boolean {
    const hasPermission = this.permissionService.has(permission);

    if (!hasPermission && errorMessage) {
      this.handlerMessage.error({ message: errorMessage });
    }

    return hasPermission;
  }
}
